import { z } from 'zod';
import { entityIdSchema, sha256Schema, utcDateTimeSchema } from '@kop/contracts';
const bindingSchema = z.object({
    tenantId: z.literal('kop'), siteId: entityIdSchema, roundId: entityIdSchema,
    // null explicitly represents the factory itself, never an inferred contractor mapping.
    contractorId: entityIdSchema.nullable(), workCategoryIds: z.array(entityIdSchema).max(50),
}).strict().refine(value => new Set(value.workCategoryIds).size === value.workCategoryIds.length, 'Work categories must be unique');
export const workerParticipationPurposeSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('opinion-collection') }).strict(),
    z.object({ kind: z.literal('final-assessment-confirmation'), assessmentId: entityIdSchema,
        assessmentVersion: z.number().int().positive(), assessmentSha256: sha256Schema }).strict(),
]);
export const workerParticipationCampaignSchema = z.object({
    campaignId: entityIdSchema, version: z.number().int().positive(), binding: bindingSchema,
    purpose: workerParticipationPurposeSchema, tokenDigest: sha256Schema,
    opensAt: utcDateTimeSchema, closesAt: utcDateTimeSchema,
    status: z.enum(['open', 'closed', 'revoked']),
    // References to an explicitly configured policy; this module invents no policy or retention period.
    collectionNoticeVersionId: entityIdSchema, retentionPolicyVersionId: entityIdSchema,
}).strict().refine(value => Date.parse(value.opensAt) < Date.parse(value.closesAt), 'Invalid publication window');
const evidenceReferenceSchema = z.object({
    manifestId: entityIdSchema, sha256: sha256Schema, serverReceiptId: entityIdSchema,
}).strict();
const signatureSchema = z.discriminatedUnion('state', [
    z.object({ state: z.literal('pending') }).strict(),
    z.object({ state: z.literal('committed'), reference: evidenceReferenceSchema }).strict(),
]);
export const workerParticipationSubmissionSchema = z.object({
    campaignId: entityIdSchema, campaignVersion: z.number().int().positive(),
    binding: bindingSchema, purpose: workerParticipationPurposeSchema, idempotencyKey: entityIdSchema,
    channel: z.enum(['link', 'paper']),
    participant: z.object({ kind: z.literal('self-declared'), identityId: z.null(), workerId: z.null(),
        name: z.string().trim().min(1).max(200), organizationName: z.string().trim().min(1).max(200) }).strict(),
    opinion: z.string().trim().max(4000),
    assessmentConfirmed: z.boolean(),
    notice: z.object({ versionId: entityIdSchema, acknowledged: z.literal(true) }).strict(),
    signature: signatureSchema,
}).strict().refine(value => value.assessmentConfirmed === (value.purpose.kind === 'final-assessment-confirmation'), 'Opinion collection is separate from explicit final assessment confirmation');
export const workerParticipationReceiptSchema = z.object({
    receiptId: entityIdSchema, immutable: z.literal(true), campaignId: entityIdSchema,
    campaignVersion: z.number().int().positive(), idempotencyKey: entityIdSchema, contentDigest: sha256Schema,
    receivedAt: utcDateTimeSchema, recordedByIdentityId: entityIdSchema.nullable(),
    evidence: evidenceReferenceSchema,
}).strict();
const reject = (code) => { throw new Error(code); };
const canonicalBinding = (value) => ({ ...value, workCategoryIds: [...value.workCategoryIds].sort() });
export function evaluateWorkerParticipation(campaignValue, submissionValue, server, priorValue = null) {
    const campaign = workerParticipationCampaignSchema.parse(campaignValue);
    const submission = workerParticipationSubmissionSchema.parse(submissionValue);
    const now = utcDateTimeSchema.parse(server.now);
    // Replays do not reopen expired or revoked collection links.
    if (campaign.status !== 'open')
        reject('PARTICIPATION_CAMPAIGN_CLOSED');
    if (Date.parse(now) < Date.parse(campaign.opensAt) || Date.parse(now) >= Date.parse(campaign.closesAt))
        reject('PARTICIPATION_WINDOW_CLOSED');
    if (!server.policiesAvailable)
        reject('PARTICIPATION_POLICY_REQUIRED');
    if (server.access.kind !== submission.channel)
        reject('PARTICIPATION_CHANNEL_MISMATCH');
    const recordedByIdentityId = server.access.kind === 'paper'
        ? entityIdSchema.parse(server.access.recorderIdentityId) : null;
    if (server.access.kind === 'link') {
        if (sha256Schema.parse(server.access.tokenDigest) !== campaign.tokenDigest)
            reject('PARTICIPATION_TOKEN_INVALID');
    }
    else if (!server.access.authorized)
        reject('PARTICIPATION_RECORDER_FORBIDDEN');
    const hash = (value) => sha256Schema.parse(server.digest(value));
    if (submission.campaignId !== campaign.campaignId || submission.campaignVersion !== campaign.version ||
        hash(canonicalBinding(submission.binding)) !== hash(canonicalBinding(campaign.binding)) ||
        hash(submission.purpose) !== hash(campaign.purpose))
        reject('PARTICIPATION_BINDING_MISMATCH');
    if (submission.notice.versionId !== campaign.collectionNoticeVersionId)
        reject('PARTICIPATION_NOTICE_CHANGED');
    if (submission.signature.state === 'pending') {
        if (priorValue != null)
            reject('PARTICIPATION_IDEMPOTENCY_CONFLICT');
        return { status: 'awaiting-evidence' };
    }
    const proof = server.verifiedEvidence;
    if (!proof || proof.campaignId !== campaign.campaignId || proof.submissionKey !== submission.idempotencyKey || proof.channel !== submission.channel ||
        hash(canonicalBinding(proof.binding)) !== hash(canonicalBinding(campaign.binding)) ||
        hash(proof.purpose) !== hash(campaign.purpose) ||
        hash(proof.reference) !== hash(submission.signature.reference))
        reject('PARTICIPATION_EVIDENCE_REQUIRED');
    const contentDigest = hash({ submission: { ...submission, binding: canonicalBinding(submission.binding) }, recordedByIdentityId });
    if (priorValue != null) {
        const prior = workerParticipationReceiptSchema.parse(priorValue);
        if (prior.campaignId !== campaign.campaignId || prior.campaignVersion !== campaign.version ||
            prior.idempotencyKey !== submission.idempotencyKey || prior.contentDigest !== contentDigest ||
            prior.recordedByIdentityId !== recordedByIdentityId || hash(prior.evidence) !== hash(submission.signature.reference) ||
            Date.parse(prior.receivedAt) < Date.parse(campaign.opensAt) || Date.parse(prior.receivedAt) >= Date.parse(campaign.closesAt) ||
            Date.parse(prior.receivedAt) > Date.parse(now))
            reject('PARTICIPATION_IDEMPOTENCY_CONFLICT');
        return { status: 'accepted', replayed: true, submission, receipt: prior };
    }
    const receipt = workerParticipationReceiptSchema.parse({ receiptId: server.receiptId, immutable: true,
        campaignId: campaign.campaignId, campaignVersion: campaign.version, idempotencyKey: submission.idempotencyKey,
        contentDigest, receivedAt: now, recordedByIdentityId, evidence: submission.signature.reference });
    // The server must atomically create submission + receipt + idempotency binding before responding.
    return { status: 'accepted', replayed: false, submission, receipt };
}
/** Called only after the server checks the authenticated campaign manager's authority. */
export function closeWorkerParticipationCampaign(value, expectedVersion, action) {
    const campaign = workerParticipationCampaignSchema.parse(value);
    if (action !== 'close' && action !== 'revoke')
        reject('PARTICIPATION_ACTION_INVALID');
    if (campaign.version !== expectedVersion)
        reject('PARTICIPATION_VERSION_CONFLICT');
    if (campaign.status === 'revoked' || (action === 'close' && campaign.status !== 'open'))
        reject('PARTICIPATION_CAMPAIGN_CLOSED');
    return workerParticipationCampaignSchema.parse({ ...campaign, version: campaign.version + 1,
        status: action === 'revoke' ? 'revoked' : 'closed' });
}
